import { ReactNode } from 'react';
import { Clock } from 'lucide-react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { cn } from '@/lib/utils';

/*
 * ServiceListRow — DESIGN_SYSTEM.md §2.4
 *
 * Selectable list row used in:
 *   - public booking step 1 (pass `value` to wire up as a Radix RadioGroup item)
 *   - admin services list (omit `value` and pass `trailing` for custom actions)
 *
 * Layout: name/desc/meta on the left, price + radio on the right.
 * Selected: brand-50 bg, brand-300 border, brand-filled radio.
 */

interface SharedProps {
  name: ReactNode;
  description?: ReactNode;
  /** Service duration in minutes. */
  duration?: number;
  /** Price already formatted for the locale. */
  price?: ReactNode;
  /** Custom trailing content (e.g. Edit / Delete). Hidden when `value` is set. */
  trailing?: ReactNode;
  /** Optional leading icon. */
  icon?: ReactNode;
  className?: string;
}

interface ServiceListRowRadioProps
  extends SharedProps,
    Omit<React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>, keyof SharedProps> {
  value: string;
}

interface ServiceListRowDivProps extends SharedProps, Omit<React.HTMLAttributes<HTMLDivElement>, keyof SharedProps> {
  value?: undefined;
}

function Inner({ name, description, duration, price, trailing, icon, isRadio }: SharedProps & { isRadio: boolean }) {
  return (
    <>
      {icon && (
        <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-md bg-surface-2 text-soft [&_svg]:h-4 [&_svg]:w-4">
          {icon}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-body font-semibold text-ink">{name}</div>
        {description && <div className="mt-0.5 line-clamp-2 text-body-sm text-soft">{description}</div>}
        {duration !== undefined && (
          <div className="mt-1 inline-flex items-center gap-1 text-body-xs text-soft">
            <Clock className="h-3 w-3" /> {duration} min
          </div>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-3">
        {price && <div className="text-body font-semibold tabular-nums text-ink">{price}</div>}
        {trailing}
        {isRadio && (
          <span
            className={cn(
              'flex h-[22px] w-[22px] items-center justify-center rounded-pill border-2 border-line-strong bg-surface transition-colors',
              'group-data-[state=checked]:border-brand-500',
            )}
          >
            <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
              <span className="h-[10px] w-[10px] rounded-pill bg-brand-500" />
            </RadioGroupPrimitive.Indicator>
          </span>
        )}
      </div>
    </>
  );
}

const SHARED_CLASS = cn(
  'group flex w-full items-start gap-3 rounded-xl border bg-surface px-4 py-3.5 text-left transition-colors',
  'hover:border-brand-300',
  'data-[state=checked]:border-brand-300 data-[state=checked]:bg-brand-50',
  'border-line',
);

export function ServiceListRow(props: ServiceListRowRadioProps | ServiceListRowDivProps) {
  const { name, description, duration, price, trailing, icon, className, value, ...rest } = props;

  if (value !== undefined) {
    return (
      <RadioGroupPrimitive.Item
        value={value}
        className={cn(SHARED_CLASS, className)}
        {...(rest as Omit<React.ComponentProps<typeof RadioGroupPrimitive.Item>, 'value'>)}
      >
        <Inner name={name} description={description} duration={duration} price={price} trailing={trailing} icon={icon} isRadio />
      </RadioGroupPrimitive.Item>
    );
  }

  return (
    <div
      className={cn(SHARED_CLASS, className)}
      {...(rest as React.HTMLAttributes<HTMLDivElement>)}
    >
      <Inner name={name} description={description} duration={duration} price={price} trailing={trailing} icon={icon} isRadio={false} />
    </div>
  );
}
